import type { FinderGroup, FinderItem, FinderMatterItem } from './matterFinder.types';

/** Same tokenising as the server, so highlights line up with what matched. */
export function tokenizeFinderQuery(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^a-z0-9@.]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .slice(0, 8);
}

/** Split `text` into [plain, hit, plain, hit…] segments for the given tokens; odd indexes are hits. */
export function highlightSegments(text: string, tokens: string[]): string[] {
  const usable = tokens.filter((t) => t.length >= 2);
  if (!text || usable.length === 0) return [text];
  const pattern = new RegExp(`(${usable.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return text.split(pattern);
}

export interface FlatRow {
  groupIndex: number;
  item: FinderItem;
}

/** Groups flattened to one keyboard-navigable list, in display order. */
export function flattenGroups(groups: FinderGroup[]): FlatRow[] {
  const out: FlatRow[] = [];
  groups.forEach((g, groupIndex) => g.items.forEach((item) => out.push({ groupIndex, item })));
  return out;
}

const STAGE_LABEL: Record<string, string> = {
  intake: 'Intake',
  review: 'Review',
  approved: 'Approved',
  active: 'Active',
  closed: 'Closed',
  rejected: 'Rejected',
};

export function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] || stage;
}

/** "Sep 9" this year, "Sep 9, 2025" otherwise, "today"/"yesterday" when close. */
export function relativeActivity(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

/** One line under the title: number · client · practice / subtype · jurisdiction. */
export function matterMetaParts(m: FinderMatterItem, opts: { hideClient?: boolean } = {}): string[] {
  const parts: string[] = [];
  if (m.matterNumber) parts.push(m.matterNumber);
  if (!opts.hideClient && m.clientName) parts.push(m.clientName);
  const practice = [m.practiceArea, m.caseSubtypeLabel || m.caseSubtype].filter(Boolean).join(' / ');
  if (practice) parts.push(practice);
  if (m.jurisdictionCode) parts.push(m.jurisdictionCode);
  return parts;
}

const LAST_MATTER_KEY = 'edgelex.matterFinder.lastMatter';

/** Per-device memory of the matter a host linked last (Billing suggests it first). */
export function rememberLastMatter(host: string, matterId: string): void {
  try {
    const raw = window.localStorage.getItem(LAST_MATTER_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[host] = matterId;
    window.localStorage.setItem(LAST_MATTER_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable: nothing to remember */
  }
}

export function lastMatterFor(host: string): string | null {
  try {
    const raw = window.localStorage.getItem(LAST_MATTER_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    return typeof map[host] === 'string' ? map[host] : null;
  } catch {
    return null;
  }
}

function agoShort(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const minutes = Math.max(0, Math.round((now.getTime() - d.getTime()) / 60_000));
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return relativeActivity(iso, now);
}

/**
 * Why a Matter is suggested, in the lawyer's terms (plan LEX_MATTER_FINDER,
 * 2026-10-12): "Pinned · Hearing Thu, Oct 15, 8:30 AM · 3 unread emails".
 * Same order as the server's For you ranking; at most three parts.
 */
export function finderReason(m: FinderMatterItem, now = new Date()): string {
  const parts: string[] = [];
  const s = m.signals;
  if (s?.pinned) parts.push('Pinned');
  if (m.nextHearingAt) {
    const d = new Date(m.nextHearingAt);
    const days = (d.getTime() - now.getTime()) / 86_400_000;
    if (!Number.isNaN(d.getTime()) && days >= -0.5 && days <= 14) {
      const when = m.nextHearingTimeKnown === false
        ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
        : d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      parts.push(`Hearing ${when}`);
    }
  }
  const chat = s?.lastChatAt ? new Date(s.lastChatAt).getTime() : 0;
  const pick = s?.lastPickedAt ? new Date(s.lastPickedAt).getTime() : 0;
  if (chat && chat >= pick) parts.push(`Lex chat ${agoShort(s!.lastChatAt!, now)}`);
  else if (pick) parts.push(`Opened ${agoShort(s!.lastPickedAt!, now)}`);
  if (s && s.unreadEmails > 0) parts.push(`${s.unreadEmails} unread email${s.unreadEmails === 1 ? '' : 's'}`);
  return parts.slice(0, 3).join(' · ');
}

/** Each Matter once, in the first group that shows it (For you and Recent overlap). */
export function dedupeFinderGroups(groups: FinderGroup[]): FinderGroup[] {
  const seen = new Set<string>();
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        const key = `${item.kind}:${item.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);
}
